import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwiperClass } from "swiper/react";
import { SwapPlanMealDetailsCard } from "../swap-plan-meal-details-card";
import { SwipeMappingMode, TinderCardSwiper } from "../tinder-card-swiper";

interface SwapPlanSwiperProps {
  alternativeMeals: Meal[];
  mealIndex?: number;
  mealData: Meal;
  mealTime: MealTime;
  mealDay: string;
  targetDate?: string;
  requestId?: string | null;
  resetSignal?: string;
  swipeMappingMode?: SwipeMappingMode;
  isExtraMeal?: boolean;
}

export const SwapPlanSwiper = ({
  alternativeMeals,
  mealData,
  mealTime,
  mealDay,
  targetDate,
  mealIndex,
  requestId,
  resetSignal,
  swipeMappingMode = "index",
  isExtraMeal,
}: SwapPlanSwiperProps) => {
  const slides = useMemo(() => {
    if (alternativeMeals.length > 0) {
      return alternativeMeals;
    }
    return [mealData];
  }, [alternativeMeals, mealData]);

  const initialIndex = useMemo(() => {
    const storedIndexString =
      typeof window === "undefined"
        ? null
        : window.localStorage.getItem(LocalStorageKeys.SWAP_PLAN_MEAL_INDEX);
    const storedIndex = storedIndexString ? parseInt(storedIndexString, 10) : undefined;
    if (storedIndex !== undefined && !Number.isNaN(storedIndex)) {
      return Math.min(Math.max(storedIndex, 0), slides.length - 1);
    }
    return 0;
  }, [slides.length]);

  const [selectedMealIndex, setSelectedMealIndex] = useState(initialIndex);
  const swiperRef = useRef<SwiperClass | null>(null);

  useEffect(() => {
    setSelectedMealIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    setSelectedMealIndex(0);
    swiperRef.current?.slideTo(0);
  }, [resetSignal]);

  const handleSlideChange = useCallback((swiper: SwiperClass) => {
    const newIndex = swiper.activeIndex;
    setSelectedMealIndex(newIndex);
  }, []);

  const handleSwiperInit = useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper;
  }, []);

  const hasMultipleSlides = slides.length > 1;
  const handleSkipToNext = useCallback(() => {
    if (swiperRef.current && hasMultipleSlides) {
      swiperRef.current.slideNext();
    }
  }, [hasMultipleSlides]);

  const selectedMeal = slides[selectedMealIndex] ?? mealData;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="flex-1">
        <TinderCardSwiper
          alternativeMeals={alternativeMeals}
          mealData={mealData}
          mealTime={mealTime}
          mealDay={mealDay}
          mealIndex={mealIndex}
          requestId={requestId}
          resetSignal={resetSignal}
          swipeMappingMode={swipeMappingMode}
          onSlideChange={handleSlideChange}
          onSwiperInit={handleSwiperInit}
        />
      </div>

      <SwapPlanMealDetailsCard
        meal={selectedMeal}
        mealTime={mealTime}
        mealDay={mealDay}
        targetDate={targetDate}
        mealIndex={mealIndex}
        currentMealId={mealData?.id ?? undefined}
        requestId={requestId}
        selectedPosition={selectedMealIndex}
        onSkipToNext={handleSkipToNext}
        isExtra={isExtraMeal}
      />
    </div>
  );
};
